import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import api, { fileUrl } from '../lib/api';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Bookmark, Play, Pause, Scissors, ArrowRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import Waveform from '../components/Waveform';
import TipButton from '../components/TipButton';
import { motion } from 'framer-motion';

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true&size=200`;

function ChalkCard({ post, active }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [liked, setLiked] = useState(post.is_liked);
  const [likes, setLikes] = useState(post.likes_count || 0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (active) {
      a.currentTime = 0;
      const p = a.play();
      if (p) p.then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      a.pause();
      setPlaying(false);
    }
  }, [active]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.currentTime);
    a.addEventListener('timeupdate', onTime);
    return () => a.removeEventListener('timeupdate', onTime);
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  const like = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(data.liked);
      setLikes(l => l + (data.liked ? 1 : -1));
    } catch { toast.error('Could not like'); }
  };

  return (
    <div
      className="h-[calc(100vh-64px)] w-full snap-start snap-always flex items-center justify-center px-4 py-6"
      data-testid={`chalk-card-${post.id}`}
    >
      <div
        className="relative w-full max-w-md h-full max-h-[800px] rounded-3xl overflow-hidden border border-[#FFB000]/20 bg-gradient-to-b from-black via-[#0a0a0a] to-black shadow-[0_0_60px_rgba(255,176,0,0.15)]"
      >
        {/* Cover / vinyl backdrop */}
        {post.cover_path ? (
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${fileUrl(post.cover_path)})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        ) : (
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            animate={{ rotate: playing ? 360 : 0 }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          >
            <div className="w-72 h-72 rounded-full bg-black border-2 border-[#FFB000]/40 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[#FFB000]" />
            </div>
          </motion.div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/60" />

        <audio ref={audioRef} src={fileUrl(post.audio_path)} preload="metadata" />

        {/* Top row: author + chalk badge */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <Link to={`/profile/${post.author?.user_id}`} className="flex items-center gap-2 group">
            <img
              src={post.author?.avatar_path ? fileUrl(post.author.avatar_path) : avatarFallback(post.author?.name)}
              alt=""
              className="w-9 h-9 rounded-full border border-[#FFB000]/40 object-cover"
            />
            <div>
              <div className="text-white text-sm font-heading font-bold group-hover:text-[#FFB000] transition-colors">{post.author?.name}</div>
              <div className="text-[#A1A1AA] text-[10px]">@{post.author?.username}</div>
            </div>
          </Link>
          <div className="px-2 py-0.5 rounded-full bg-[#FFB000] text-black text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
            <Scissors size={9} /> Chalk
          </div>
        </div>

        {/* Big play button */}
        <button
          onClick={toggle}
          className="absolute inset-0 flex items-center justify-center group"
          aria-label={playing ? 'Pause' : 'Play'}
          data-testid={`chalk-play-${post.id}`}
        >
          <div className={`w-20 h-20 rounded-full flex items-center justify-center bg-black/70 border-2 border-[#FFB000] text-[#FFB000] transition-opacity ${playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
            {playing ? <Pause size={30} fill="#FFB000" /> : <Play size={30} fill="#FFB000" style={{ marginLeft: 4 }} />}
          </div>
        </button>

        {/* Bottom content */}
        <div className="absolute bottom-0 left-0 right-0 p-5 space-y-3">
          <div>
            <div className="text-white font-heading font-black text-2xl leading-tight">{post.title}</div>
            {post.description && <div className="text-[#A1A1AA] text-xs mt-1 line-clamp-2">{post.description}</div>}
          </div>
          <Waveform
            isPlaying={playing}
            progress={progress}
            duration={post.duration || 15}
            bars={48}
            peaks={post.waveform}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={like} className={`btn-ghost flex items-center gap-1.5 ${liked ? 'text-[#EF4444]' : ''}`} data-testid={`chalk-like-${post.id}`}>
                <Heart size={18} fill={liked ? '#EF4444' : 'none'} />
                <span className="text-xs font-bold">{likes}</span>
              </button>
              <button className="btn-ghost flex items-center gap-1.5">
                <MessageCircle size={18} />
                <span className="text-xs font-bold">{post.comments_count || 0}</span>
              </button>
              {post.author && <TipButton recipient={post.author} postId={post.id} />}
            </div>
            {post.parent_post_id && (
              <Link
                to={`/dashboard#${post.parent_post_id}`}
                className="text-xs text-[#FFB000] font-bold flex items-center gap-1 hover:text-white"
                data-testid={`chalk-source-${post.id}`}
              >
                Full mix <ArrowRight size={12} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ChalkFeed() {
  const [chalks, setChalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    api.get('/chalks/feed').then(r => {
      setChalks(r.data);
      if (r.data[0]) setActiveId(r.data[0].id);
    }).finally(() => setLoading(false));
  }, []);

  const onScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll('[data-testid^="chalk-card-"]');
    const midY = el.scrollTop + el.clientHeight / 2;
    let best = null, bestDist = Infinity;
    cards.forEach(c => {
      const cardMid = c.offsetTop + c.offsetHeight / 2;
      const d = Math.abs(cardMid - midY);
      if (d < bestDist) { bestDist = d; best = c; }
    });
    if (best) {
      const id = best.getAttribute('data-testid').replace('chalk-card-', '');
      if (id !== activeId) setActiveId(id);
    }
  }, [activeId]);

  return (
    <Layout>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-1">Chalks</div>
          <h1 className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter">15-second highlights</h1>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold hidden md:flex items-center gap-1"><ChevronDown size={12} /> Scroll for more</div>
      </div>

      {loading && <div className="text-[#A1A1AA] text-sm py-16 text-center">Loading chalks…</div>}
      {!loading && chalks.length === 0 && (
        <div className="post-card p-10 text-center">
          <Scissors size={32} className="mx-auto mb-3 text-[#FFB000]/50" />
          <div className="text-white font-heading font-bold">No chalks yet</div>
          <div className="text-[#A1A1AA] text-sm mt-1">Cut a 15-second highlight from any mix in your feed to be the first.</div>
        </div>
      )}

      {!loading && chalks.length > 0 && (
        <div
          ref={containerRef}
          onScroll={onScroll}
          className="h-[calc(100vh-140px)] overflow-y-scroll snap-y snap-mandatory rounded-3xl"
          data-testid="chalk-feed-container"
        >
          {chalks.map(c => <ChalkCard key={c.id} post={c} active={c.id === activeId} />)}
        </div>
      )}
    </Layout>
  );
}
